import {
  Body, Controller, Delete, Get, Param, Query,
  Patch, Post, Put, Req, UseGuards, HttpCode,
} from '@nestjs/common';
import { ResubmitMerchantRegistrationRequest } from '@cart/shared-types';
import { AuthGuard, GuardedRequest } from '../auth/auth.guard';
import { DemoModeGuard } from '../auth/demo-mode.guard';
import { MerchantService } from './merchant.service';

@Controller('merchant')
@UseGuards(AuthGuard, DemoModeGuard)
export class MerchantController {
  constructor(private readonly merchantService: MerchantService) {}

  @Get('overview')
  getOverview(@Req() req: GuardedRequest) {
    return this.merchantService.getOverview(req.user.id);
  }

  // Rejected merchants only (service enforces) — edits business details and
  // flips the org back to 'pending'. AuthGuard only, same as every other
  // route on this controller: a pre-approval merchant has no StoreStaff row
  // so AdminAccessGuard would incorrectly 403 them.
  @Patch('registration')
  resubmitRegistration(@Body() body: ResubmitMerchantRegistrationRequest, @Req() req: GuardedRequest) {
    return this.merchantService.resubmitRegistration(req.user.id, body);
  }

  @Get('staff')
  getAllStaff(@Req() req: GuardedRequest) {
    return this.merchantService.getAllStaff(req.user.id);
  }

  @Get('stores/:storeId/staff')
  getStoreStaff(@Param('storeId') storeId: string, @Req() req: GuardedRequest) {
    return this.merchantService.getStoreStaff(storeId, req.user.id);
  }

  @Post('stores/:storeId/staff')
  addStaff(
    @Param('storeId') storeId: string,
    @Body() body: { email: string; role: string },
    @Req() req: GuardedRequest,
  ) {
    return this.merchantService.addStaff(storeId, body.email, body.role ?? 'store_manager', req.user.id);
  }

  @Patch('stores/:storeId/staff/:staffId')
  updateStaff(
    @Param('staffId') staffId: string,
    @Body() body: { role: string },
    @Req() req: GuardedRequest,
  ) {
    return this.merchantService.updateStaff(staffId, body.role, req.user.id);
  }

  @Delete('stores/:storeId/staff/:staffId')
  removeStaff(@Param('staffId') staffId: string, @Req() req: GuardedRequest) {
    return this.merchantService.removeStaff(staffId, req.user.id);
  }

  @Get('stores/:storeId/staff/:staffId/permissions')
  getStaffPermissions(@Param('staffId') staffId: string, @Req() req: GuardedRequest) {
    return this.merchantService.getStaffPermissions(staffId, req.user.id);
  }

  // ── Store CRUD ─────────────────────────────────────────────────────────────

  @Get('stores/check-subdomain')
  checkSubdomain(@Query('value') value: string) {
    return this.merchantService.checkSubdomain(value ?? '');
  }

  @Post('stores')
  createStore(
    @Body() body: { name: string; subdomain: string; city?: string; state?: string; country?: string; currency?: string; timezone?: string },
    @Req() req: GuardedRequest,
  ) {
    return this.merchantService.createStore(req.user.id, body);
  }

  @Patch('stores/:storeId')
  updateStore(
    @Param('storeId') storeId: string,
    @Body() body: { name?: string; city?: string; state?: string; country?: string; currency?: string; timezone?: string },
    @Req() req: GuardedRequest,
  ) {
    return this.merchantService.updateStore(storeId, req.user.id, body);
  }

  @Delete('stores/:storeId')
  @HttpCode(200)
  deleteStore(@Param('storeId') storeId: string, @Req() req: GuardedRequest) {
    return this.merchantService.deleteStore(storeId, req.user.id);
  }

  @Patch('stores/:storeId/activate')
  activateStore(@Param('storeId') storeId: string, @Req() req: GuardedRequest) {
    return this.merchantService.toggleStoreActive(storeId, true, req.user.id);
  }

  @Patch('stores/:storeId/deactivate')
  deactivateStore(@Param('storeId') storeId: string, @Req() req: GuardedRequest) {
    return this.merchantService.toggleStoreActive(storeId, false, req.user.id);
  }

  @Put('stores/:storeId/staff/:staffId/permissions')
  setStaffPermissions(
    @Param('staffId') staffId: string,
    @Body() body: { permissions: string[]; granted?: boolean },
    @Req() req: GuardedRequest,
  ) {
    return this.merchantService.setStaffPermissions(
      staffId, body.permissions, body.granted ?? true, req.user.id,
    );
  }
}
